import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  ArrowRight, 
  Clock, 
  DollarSign, 
  FileText, 
  Shield, 
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  CreditCard,
  RefreshCw,
  Mail
} from "lucide-react";

const RefundPolicy = () => {
  const refundScenarios = [
    {
      title: "Standard Document Generation Services",
      icon: FileText,
      timeframe: "30 days",
      conditions: [
        "Full refund available within 30 days of purchase if unsatisfied with service",
        "Service must not have been completed (document not generated/downloaded)",
        "Refund request must include reason for dissatisfaction",
        "Processing time: 5-7 business days"
      ],
      eligible: true
    },
    {
      title: "Add-on Services (Document Review & Filing Support)",
      icon: Shield,
      timeframe: "30 days", 
      conditions: [
        "Document Review ($149): Full refund if review not provided within promised timeframe",
        "Filing Support ($99): Partial refund available if service partially completed",
        "Must request refund before filing documents with court/agency",
        "Professional services rendered are non-refundable after completion"
      ],
      eligible: true
    },
    {
      title: "Enterprise & Trial Offerings",
      icon: CreditCard,
      timeframe: "30 days",
      conditions: [
        "Enterprise plans: Pro-rated refund for unused portion of subscription",
        "7-day trials: No charges, automatic cancellation available anytime",
        "Enterprise custom work: 50% refund if cancelled within 7 days of contract",
        "Dedicated support services non-refundable after 30 days"
      ],
      eligible: true
    }
  ];

  const nonRefundableScenarios = [
    {
      title: "After Document Download/Completion",
      description: "Once you have downloaded your completed legal documents, no refund is available as the service has been fully rendered."
    },
    {
      title: "Used or Filed Documents", 
      description: "Documents that have been filed with courts, agencies, or used in legal proceedings cannot be refunded."
    },
    {
      title: "Custom Legal Work",
      description: "Specialized legal document preparation that required significant customization beyond standard forms."
    },
    {
      title: "Third-Party Services",
      description: "Any costs related to third-party services (court filing fees, notarization, etc.) are non-refundable."
    },
    {
      title: "Abandoned Services",
      description: "Services where customer did not respond to requests for information for 60+ days are considered complete and non-refundable."
    }
  ];

  const refundProcess = [
    {
      step: 1,
      title: "Contact Support",
      description: "Email support@formforgepro.com with your order number and refund reason",
      icon: Mail
    },
    {
      step: 2, 
      title: "Review Process",
      description: "Our team reviews your request within 2 business days",
      icon: FileText
    },
    {
      step: 3,
      title: "Approval & Processing", 
      description: "Approved refunds processed within 5-7 business days to original payment method",
      icon: RefreshCw
    }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <RefreshCw className="h-12 w-12 text-teal-500 mr-4" />
              <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-200 px-4 py-2 text-lg">
                30-Day Satisfaction Guarantee
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Refund Policy
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
              At FormForgePro, we stand behind our legal document preparation services. 
              If you're not completely satisfied, our 30-day refund policy ensures you're protected.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Overview */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Alert className="border-teal-200 bg-teal-50">
            <CheckCircle className="h-5 w-5 text-teal-500" />
            <AlertDescription className="text-teal-800 font-medium">
              <strong>Our Promise:</strong> We offer a comprehensive 30-day satisfaction guarantee on all our services. 
              If you're not completely satisfied with our legal document preparation service, we'll make it right or provide a full refund.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* Refund Scenarios */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Refund Eligibility
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Understanding when and how refunds apply to different services
            </p>
          </div>
          
          <div className="space-y-8">
            {refundScenarios.map((scenario, index) => {
              const IconComponent = scenario.icon;
              return (
                <Card key={index} className="border-2 border-teal-200 hover:border-teal-300 transition-colors duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <CardTitle className="text-xl text-slate-900">
                            {scenario.title}
                          </CardTitle>
                          <div className="flex items-center space-x-2 mt-1">
                            <Clock className="h-4 w-4 text-teal-500" />
                            <span className="text-sm text-teal-600 font-medium">
                              {scenario.timeframe} refund window
                            </span>
                          </div>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Eligible
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {scenario.conditions.map((condition, conditionIndex) => (
                        <li key={conditionIndex} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{condition}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Non-Refundable Items */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Non-Refundable Scenarios
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Important exceptions where refunds cannot be provided
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {nonRefundableScenarios.map((scenario, index) => (
              <Card key={index} className="border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3">
                    <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">
                        {scenario.title}
                      </h3>
                      <p className="text-gray-700 text-sm">
                        {scenario.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Refund Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How to Request a Refund
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple 3-step process to request your refund
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {refundProcess.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="text-center relative">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-teal-600" />
                    </div>
                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              asChild
              size="lg"
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4"
            >
              <a href="mailto:support@formforgepro.com?subject=Refund Request">
                <Mail className="mr-2 h-5 w-5" />
                Request Refund
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Partial Refund Policy */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Partial Refund Policy
            </h2>
            <p className="text-xl text-gray-600">
              For services that have been partially completed
            </p>
          </div>
          
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                    <DollarSign className="h-5 w-5 text-green-500 mr-2" />
                    Unused Services Refund
                  </h3>
                  <p className="text-gray-700 mb-4">
                    For subscription services or packages, we provide pro-rated refunds for the unused portion 
                    of your service based on the time remaining in your subscription period.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Enterprise subscriptions: Refund calculated based on days remaining</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Multi-document packages: Refund for unused document credits</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Add-on services: 50% refund if cancelled before completion</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                    Work-in-Progress Refunds
                  </h3>
                  <p className="text-gray-700 mb-4">
                    If you wish to cancel a service that is currently being prepared, refund amounts 
                    depend on the stage of completion:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-green-600">Initial Stage (0-25%)</div>
                        <div className="text-gray-600">90% Refund</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-yellow-600">In Progress (25-75%)</div>
                        <div className="text-gray-600">50% Refund</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-red-600">Near Completion (75%+)</div>
                        <div className="text-gray-600">No Refund</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Processing & Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Refund Processing Timeline
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              What to expect after your refund request is approved
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              <div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Mail className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Request Submitted</h3>
                <p className="text-sm text-gray-600">Immediate confirmation email sent</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Review Process</h3>
                <p className="text-sm text-gray-600">1-2 business days for evaluation</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Approval Notification</h3>
                <p className="text-sm text-gray-600">Email confirmation of approved refund</p>
              </div>
              <div>
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CreditCard className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">Refund Processed</h3>
                <p className="text-sm text-gray-600">5-7 business days to original payment method</p>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Alert className="border-blue-200 bg-blue-50 max-w-2xl mx-auto">
                <AlertTriangle className="h-5 w-5 text-blue-500" />
                <AlertDescription className="text-blue-800">
                  <strong>Processing Note:</strong> Refunds are processed to your original payment method. 
                  Credit card refunds may take 5-10 business days to appear on your statement depending on your bank.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Questions About Our Refund Policy?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Our customer support team is here to help you understand our refund policy 
            and assist with any refund requests.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-4 rounded-lg text-lg"
            >
              <Link to="/help">
                Contact Support
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold px-8 py-4 rounded-lg text-lg"
            >
              <a href="mailto:support@formforgepro.com?subject=Refund Policy Question">
                Email Support
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600">
            This refund policy was last updated on January 1, 2025. We reserve the right to modify 
            this policy at any time. Please review it periodically for changes. Changes become effective immediately upon posting.
          </p>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;